export const CREDIT_CARD_WIDTH_CM = 8.56;

export function calculatePixelsPerCentimeter(referencePixels: number, referenceCentimeters: number): number {
  if (referencePixels <= 0 || referenceCentimeters <= 0 || !Number.isFinite(referencePixels) || !Number.isFinite(referenceCentimeters)) {
    throw new RangeError('reference dimensions must be finite positive numbers');
  }
  return referencePixels / referenceCentimeters;
}

export function visualDegreesToCentimeters(angleDegrees: number, viewingDistanceCm: number): number {
  if (!Number.isFinite(angleDegrees) || angleDegrees < 0 || angleDegrees >= 180 || viewingDistanceCm <= 0) {
    throw new RangeError('visual angle and viewing distance are outside physical bounds');
  }
  return 2 * viewingDistanceCm * Math.tan((angleDegrees * Math.PI) / 360);
}

export function centimetersToVisualDegrees(sizeCm: number, viewingDistanceCm: number): number {
  if (!Number.isFinite(sizeCm) || sizeCm < 0 || viewingDistanceCm <= 0) {
    throw new RangeError('size and viewing distance must be finite and non-negative/positive');
  }
  return (2 * Math.atan(sizeCm / (2 * viewingDistanceCm)) * 180) / Math.PI;
}

export function visualDegreesToPixels(
  angleDegrees: number,
  viewingDistanceCm: number,
  pixelsPerCentimeter: number,
): number {
  if (pixelsPerCentimeter <= 0) throw new RangeError('pixelsPerCentimeter must be positive');
  return visualDegreesToCentimeters(angleDegrees, viewingDistanceCm) * pixelsPerCentimeter;
}

export function pixelsToVisualDegrees(
  pixels: number,
  viewingDistanceCm: number,
  pixelsPerCentimeter: number,
): number {
  if (pixels < 0 || pixelsPerCentimeter <= 0) throw new RangeError('pixel values must be valid');
  return centimetersToVisualDegrees(pixels / pixelsPerCentimeter, viewingDistanceCm);
}

export function arcminutesToPixels(
  arcminutes: number,
  viewingDistanceCm: number,
  pixelsPerCentimeter: number,
): number {
  return visualDegreesToPixels(arcminutes / 60, viewingDistanceCm, pixelsPerCentimeter);
}

export function angularSpeedToPixelsPerSecond(
  degreesPerSecond: number,
  viewingDistanceCm: number,
  pixelsPerCentimeter: number,
): number {
  return visualDegreesToPixels(degreesPerSecond, viewingDistanceCm, pixelsPerCentimeter);
}

export interface PerspectivePoint {
  x: number;
  y: number;
  scale: number;
  visible: boolean;
}

/** Pinhole projection for generated pitch scenes. Coordinates are metres from the camera. */
export function projectPerspectivePoint(
  lateralMeters: number,
  forwardMeters: number,
  heightMeters: number,
  viewportWidth: number,
  viewportHeight: number,
  horizontalFieldOfViewDegrees = 90,
  cameraHeightMeters = 1.65,
): PerspectivePoint {
  if (
    viewportWidth <= 0 ||
    viewportHeight <= 0 ||
    horizontalFieldOfViewDegrees <= 0 ||
    horizontalFieldOfViewDegrees >= 180
  ) {
    throw new RangeError('invalid viewport or field of view');
  }
  if (forwardMeters <= 0) {
    return { x: Number.NaN, y: Number.NaN, scale: 0, visible: false };
  }
  const focalLength = viewportWidth / (2 * Math.tan((horizontalFieldOfViewDegrees * Math.PI) / 360));
  const x = viewportWidth / 2 + (lateralMeters * focalLength) / forwardMeters;
  const y = viewportHeight / 2 - ((heightMeters - cameraHeightMeters) * focalLength) / forwardMeters;
  const scale = focalLength / forwardMeters;
  return { x, y, scale, visible: x >= 0 && x <= viewportWidth && y >= 0 && y <= viewportHeight };
}
